// @flow strict-local

import type {
  SchedulerTemplateWithExternals,
  SessionLink,
} from 'src/types/scheduler';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import compareAsc from 'date-fns/compareAsc';

import {ApiError} from 'src/utils/errors';
import {CalendarLoader} from 'src/components/lib/loading/calendar.jsx';
import * as api from 'src/utils/api-no-store';
import useNoStoreApi from 'src/hooks/useNoStoreApi';
import {isLanguageSupported} from 'src/utils/language-support';

import {
  errorMap,
  getErrorMessagesMap,
  Container,
  SuccessPage,
  NotFoundPage,
  BookingHeader,
  BookingBody,
  BookingBox,
  MaintenancePage,
  APIFailed,
  BookingLinkIsAlreadyUsed,
} from './common.jsx';

import {
  DesktopSchedulingDatePicker,
  MobileSchedulingDatePicker,
} from './date-pickers.jsx';
import {ConfirmBookingDetails} from './confirm-details.jsx';
import NoAvailability from './no-availability.jsx';

import {useTemplateAvailability} from './use-template-availability.js';
import {useIsMobile} from '../useIsMobile';

import css from '../booking.css';


export const BookingApp = ({
  agentId,
  templateSuffix,
  timezone,
}: {
  agentId?: string,
  templateSuffix: string,
  timezone: string,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const isMobile = useIsMobile();
  const suffix = [agentId, templateSuffix].filter(Boolean).join('/');
  const templateResource = useNoStoreApi<SchedulerTemplateWithExternals>(
    `scheduler/event-template/by-url-suffix/${suffix}`,
  );
  const [linkOwnerLanguage, setLinkOwnerLanguage] = React.useState(
    labelI18nInstance.language,
  );

  const template = templateResource.result;
  const params = new URLSearchParams(window.location.search.slice(1));
  const [sessionId, _setSessionId] = React.useState<?string>(
    params.get('session_id'),
  );
  const sessionResource = useNoStoreApi<SessionLink>(
    sessionId ? `scheduler/session-link/${sessionId}` : null,
  );
  const hasBrandSettingsInSession = sessionResource.result?.brand_settings?.id;
  const brand = hasBrandSettingsInSession
    ? sessionResource.result?.brand_settings
    : template?.brand_settings;

  React.useEffect(() => {
    const languageFromSession = sessionResource.result?.guest_language;
    if (languageFromSession && isLanguageSupported(languageFromSession)) {
      setLinkOwnerLanguage(languageFromSession);
    }
  }, [sessionResource.result]);

  if (templateResource.isLoading || sessionResource.isLoading) {
    return null;
  }

  const renderBookingContent = () => {
    if (
      sessionResource.error instanceof ApiError &&
      sessionResource.error.response.status === 500
    ) {
      return <APIFailed />;
    }

    if (sessionResource.result && sessionResource.result.is_expired) {
      return <BookingLinkIsAlreadyUsed />;
    }

    if (
      templateResource.error instanceof ApiError &&
      templateResource.error.response.status === 503
    ) {
      return <MaintenancePage />;
    }

    if (templateResource.error || !templateResource.result) {
      return <NotFoundPage />;
    }

    return (
      <AvailabilityCheck
        template={templateResource.result}
        timezone={timezone}
        session={sessionResource.result ?? undefined}
        brand={brand}
      />
    );
  };

  return (
    <Container
      brand={brand}
      persistLanguage={false}
      language={linkOwnerLanguage}
    >
      <BookingBody className={isMobile ? css.mobileOnly : css.desktopOnly}>
        {renderBookingContent()}
      </BookingBody>
    </Container>
  );
};

function AvailabilityCheck({template, timezone, session, brand}) {
  const {
    eventDates,
    availabilityByDate,
    isAvailabilityLoading,
    hasAvailability,
    availability,
    error,
  } = useTemplateAvailability({
    templateId: template.id,
    timezone,
    extraOptions: session ? {source_id: session.source_id} : {},
  });

  if (error instanceof ApiError && error.response.status === 500) {
    return <APIFailed />;
  }

  return isAvailabilityLoading ? (
    <CalendarLoader className={css.loadingCalendar} />
  ) : hasAvailability ? (
    <LoadedBookingApp
      template={template}
      timezone={timezone}
      eventDates={eventDates}
      availabilityByDate={availabilityByDate}
      isAvailabilityLoading={isAvailabilityLoading}
      session={session}
      brand={brand}
      availability={availability}
    />
  ) : (
    <NoAvailability
      templateId={template.id}
      color={brand?.button_color}
      sessionId={session?.id}
    />
  );
}

type ErrorType =
  | 'unavailableTimeSlot'
  | 'sameTemplateEventsFound'
  | 'phoneNumberInvalid'
  | 'emailInvalid'
  | 'invalidQuestionAnswer'
  | 'timeSlotNotAvailable'
  | 'timeSlotAlreadyBooked'
  | 'unknownError';

function LoadedBookingApp({
  template,
  timezone,
  eventDates,
  availabilityByDate,
  isAvailabilityLoading,
  session,
  availability,
  brand,
}) {
  const isMobile = useIsMobile();
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const candidateLanguage = labelI18nInstance.language;

  const [isPreview, setPreview] = React.useState(false);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search.slice(1));
    setPreview(params.has('preview'));
  });

  const [mode, setMode] = React.useState<'booking' | 'details' | 'completed'>(
    'booking',
  );
  const [error, setError] = React.useState<ErrorType | null>(null);

  const [appointment, setAppointment] = React.useState<?{
    bookingDate: string,
    bookingTime: string,
  }>(null);

  const templateId = template.id;

  const handleConfirmBooking = async ({
    name: guest_name,
    email: guest_email,
    phone: guest_phone,
    date,
    time: start_time,
    additionalQuestionAnswers: additional_question_answers,
  }: {
    name: string,
    email: string,
    phone: string,
    date: string,
    time: string,
    additionalQuestionAnswers: {question: string, answer: string | string[]}[],
  }) => {
    if (isPreview) {
      setMode('completed');
      return;
    }
    const firstDate = Object.keys(availability || {})[0];
    const firstTimes = availability?.[firstDate];

    const payload = {
      session_id: session?.id,
      template_id: templateId,
      viewer_timezone: timezone,
      date,
      start_time,
      guest_name,
      guest_email,
      guest_phone,
      additional_question_answers,
      first_available_datetime:
        firstTimes && `${firstDate} ${firstTimes[0].start_time}`,
      guest_language: candidateLanguage,
    };

    try {
      await api.post('scheduler/events', payload);
    } catch (error) {
      const {responseBody} = error || {};
      const {errors = []} = responseBody || {};
      error.handled = true;
      handleError(errors);
      setMode('booking');
      throw error;
    }
    setMode('completed');
  };

  const handleError = (errors) => {
    for (const error of errors) {
      if (errorMap[error]) {
        setError(errorMap[error]);
        return;
      }
    }
    setError('unknownError');
  };

  const sortedDates = [...eventDates].sort(compareAsc);
  const errorMessagesMap = getErrorMessagesMap(t);

  const [firstDatePicked, setPickFirstDate] = React.useState(false);
  React.useEffect(() => {
    if (!firstDatePicked) {
      if (sortedDates[0] !== undefined) {
        setSelectedDate(sortedDates[0]);
        setCalendarDate(sortedDates[0]);

        setPickFirstDate(true);
      }
    }
  }, [sortedDates]);

  const [selectedDate, setSelectedDate] = React.useState(new Date());
  const [calendarDate, setCalendarDate] = React.useState(new Date());

  React.useEffect(() => {
    setError(null);
  }, [selectedDate]);

  const dateFromAppointment = ({
    bookingDate,
    bookingTime,
  }: {
    bookingDate: string,
    bookingTime: string,
  }) => {
    const [y, m, d] = bookingDate.split('-').map((x) => parseInt(x, 10));
    const [hr, min] = bookingTime.split(':').map((x) => parseInt(x, 10));
    return new Date(y, m - 1, d, hr, min);
  };

  const {additional_questions} = template;
  const {button_color} = brand ?? {};

  const handleBookOrConfirmTimeslot = async (bookingDate, bookingTime) => {
    setAppointment({bookingDate, bookingTime});

    if (
      session?.id == null ||
      !areGuestDetailsComplete(session) ||
      !areAdditionalQuestionsComplete(session)
    ) {
      setMode('details');
    } else {
      await handleConfirmBooking({
        name: session.guest_name,
        email: session.guest_email,
        phone: session.guest_phone,
        date: bookingDate,
        time: bookingTime,
        // $FlowFixMe[incompatible-call] additional_question_answers is valid here
        additionalQuestionAnswers: session.additional_question_answers,
      });
    }
  };

  const areGuestDetailsComplete = (session) => {
    return (
      session.guest_email != null &&
      session.guest_name != null &&
      session.guest_phone != null
    );
  };

  const areAdditionalQuestionsComplete = (session) => {
    return additional_questions.every((question) => {
      return session.additional_question_answers.some(
        (answer) => answer.question === question.question,
      );
    });
  };

  let content;
  if (mode === 'booking' && templateId) {
    const Picker = isMobile
      ? MobileSchedulingDatePicker
      : DesktopSchedulingDatePicker;
    const picker = (
      <Picker
        onConfirmTimeslot={handleBookOrConfirmTimeslot}
        eventDates={eventDates}
        availabilityByDate={availabilityByDate}
        isAvailabilityLoading={isAvailabilityLoading}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        calendarDate={calendarDate}
        setCalendarDate={setCalendarDate}
        color={button_color}
        noAvailabilityContent={
          <>
            <p>{`${t('No_times_available', 'No times available')}.`}</p>
          </>
        }
        errorContent={
          error && <p className={css.errorContent}>{errorMessagesMap[error]}</p>
        }
      />
    );
    content = isMobile ? <BookingBox>{picker}</BookingBox> : picker;
  }

  return (
    <React.Fragment>
      <BookingHeader>
        {mode === 'booking' &&
          t(
            'Please_select_a_time_slot_to_schedule_a_meeting',
            'Please select a time slot to schedule a meeting',
          )}
        {mode === 'details' &&
          t(
            'Please_provide_the_following_information',
            'Please provide the following information',
          )}
      </BookingHeader>
      {content}
      {mode === 'details' && templateId && (
        <ConfirmBookingDetails
          appointment={appointment}
          templateId={templateId}
          onConfirmBooking={handleConfirmBooking}
          additionalQuestions={additional_questions}
          color={button_color}
          session={session}
        />
      )}
      {mode === 'completed' && appointment && (
        <SuccessPage date={dateFromAppointment(appointment)} />
      )}
    </React.Fragment>
  );
}
